import React, { PropsWithChildren, ReactElement, ReactNode, useContext } from 'react';
import { LinkComponentType, Product } from '@bucketplace/common';

import { RankingFilterType } from '@/editorComponents/ProductList/types';

import { LogDataFormGroupProps, UnitNode } from '../../components';

export interface StoreItemFilter {
  type: RankingFilterType;
  ids: string[];
  name: string;
}

export interface BestRankingStoreItemArgs {
  type: RankingFilterType;
  filter?: StoreItemFilter | null;
}

export interface BestRankingStoreItems {
  items: Product[];
  chips: StoreItemFilter[];
}

export interface StoreItemsContextValue {
  /** 개발환경에 따라 달라지는 link 컴포넌트를 custom 합니다.
   * ex) CRA시 react-router-dom/Link 사용
   * next 사용시 next/Link 사용
   */
  linkComponent?: LinkComponentType;
  /**
   * 상품들을 조회합니다.
   * @param ids 조회할 상품 id들
   * @return 조회된 상품 데이터들
   */
  getStoreItems?: (ids: string[]) => Promise<Product[]> | Product[];
  getBestRankingStoreItems?: (
    args: BestRankingStoreItemArgs
  ) => Promise<BestRankingStoreItems> | BestRankingStoreItems;
  /**
   * 개인화 추천 상품 목록을 조회합니다.
   * 비로그인 등의 이유로 조회할 수 없는 경우 null을 반환하면
   * renderPersonalizedStoreItemsFallback에서 반환한 UI가 대신 노출됩니다.
   */
  getPersonalizedStoreItems?: () => Promise<Product[] | null> | Product[] | null;
  /**
   * 개인화 추천 상품을 조회할 수 없을 때(getPersonalizedStoreItems가 null을 반환)
   * 상품 목록 대신 노출할 UI를 custom 합니다. 지정하지 않으면 아무것도 노출되지 않습니다.
   */
  renderPersonalizedStoreItemsFallback?: () => ReactNode;
  /**
   * 상품 id에 해당하는 상세 페이지로 이동합니다.
   * @param productId 이동한 상품 id
   * @return 상품 상세 페이지 URL
   */
  getStoreItemHref?: (productId: number) => string;
  /** 로그 데이터를 custom할 때 사용합니다. */
  setLogData?: (unitNode: UnitNode) => LogDataFormGroupProps;
  experimentType?: string;
}

const StoreItemsContext = React.createContext<StoreItemsContextValue>({});
export const useStoreItemsContext = (): StoreItemsContextValue => useContext(StoreItemsContext);

export function StoreItemsContextProvider({
  children,
  value = {},
}: PropsWithChildren<{ value?: StoreItemsContextValue }>): ReactElement {
  return (
    <StoreItemsContext.Provider value={value}>{ children }</StoreItemsContext.Provider>
  );
}
